import { useEffect, useState } from 'react'
import { useT } from '@/i18n'
import { useToast } from '@/providers/UiProviders'
import { Dialog } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Field, Input, Select } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { ApiError } from '@/lib/api'
import { createGift, fetchStickerSets, type AdminStickerSet, type AdminGiftRow } from './giftApi'

export function AddGiftDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean
  onClose: () => void
  onCreated: (gift: AdminGiftRow) => void
}) {
  const t = useT()
  const { toast } = useToast()

  const [title, setTitle] = useState('')
  const [stars, setStars] = useState('')
  const [convertStars, setConvertStars] = useState('')
  const [upgradeStars, setUpgradeStars] = useState('')
  const [limited, setLimited] = useState(true)
  const [availabilityTotal, setAvailabilityTotal] = useState('')

  const [stickerSets, setStickerSets] = useState<AdminStickerSet[]>([])
  const [stickerSetId, setStickerSetId] = useState('')
  const [documentId, setDocumentId] = useState('')
  const [loadingSets, setLoadingSets] = useState(false)

  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    // Reset on every open - a leftover value from a previous submit shouldn't linger.
    setTitle('')
    setStars('')
    setConvertStars('')
    setUpgradeStars('')
    setLimited(true)
    setAvailabilityTotal('')
    setStickerSetId('')
    setDocumentId('')
    setError(null)

    const controller = new AbortController()
    setLoadingSets(true)
    fetchStickerSets('', controller.signal)
      .then(setStickerSets)
      .catch(() => {})
      .finally(() => setLoadingSets(false))
    return () => controller.abort()
  }, [open])

  const chosenSet = stickerSets.find((s) => String(s.sticker_set_id) === stickerSetId)

  async function handleSubmit() {
    setError(null)
    const starsNum = Number(stars)
    const documentIdNum = Number(documentId)
    if (!title.trim() || !starsNum || starsNum <= 0 || !documentIdNum) {
      setError(t.gifts.fillRequired)
      return
    }

    setSubmitting(true)
    try {
      const gift = await createGift({
        title: title.trim(),
        document_id: documentIdNum,
        stars: starsNum,
        convert_stars: Number(convertStars) || 0,
        upgrade_stars: Number(upgradeStars) || 0,
        limited,
        availability_total: limited ? Number(availabilityTotal) || null : null,
      })
      toast(t.gifts.createSuccess, { tone: 'success' })
      onCreated(gift)
      onClose()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t.gifts.addGiftTitle}
      width="md"
      footer={
        <>
          <div className="flex-1" />
          <Button variant="default" onClick={onClose}>
            {t.common.cancel}
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={submitting}>
            {t.gifts.addGift}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Field label={t.col.title} required>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={64} />
        </Field>

        <div className="grid grid-cols-3 gap-3">
          <Field label={t.gifts.stars} required>
            <Input type="number" min={1} value={stars} onChange={(e) => setStars(e.target.value)} />
          </Field>
          <Field label={t.gifts.convertStars}>
            <Input type="number" min={0} value={convertStars} onChange={(e) => setConvertStars(e.target.value)} />
          </Field>
          <Field label={t.gifts.upgradeStars} hint={t.gifts.upgradeStarsHint}>
            <Input type="number" min={0} value={upgradeStars} onChange={(e) => setUpgradeStars(e.target.value)} />
          </Field>
        </div>

        <div className="flex items-center gap-2">
          <Checkbox id="limited" checked={limited} onChange={(e) => setLimited(e.target.checked)} />
          <label htmlFor="limited" className="cursor-pointer text-sm text-secondary">
            {t.gifts.limited}
          </label>
        </div>

        {limited && (
          <Field label={t.gifts.availabilityTotal} required>
            <Input
              type="number"
              min={1}
              value={availabilityTotal}
              onChange={(e) => setAvailabilityTotal(e.target.value)}
            />
          </Field>
        )}

        <Field label={t.gifts.stickerSet} required>
          <Select
            value={stickerSetId}
            onChange={(e) => {
              setStickerSetId(e.target.value)
              setDocumentId('')
            }}
            disabled={loadingSets}
          >
            <option value="">{t.gifts.stickerSetPlaceholder}</option>
            {stickerSets.map((s) => (
              <option key={s.sticker_set_id} value={s.sticker_set_id}>
                {s.title} ({s.document_ids.length})
              </option>
            ))}
          </Select>
        </Field>

        {chosenSet && (
          <Field label={t.gifts.document} required>
            <Select value={documentId} onChange={(e) => setDocumentId(e.target.value)}>
              <option value="">{t.gifts.documentPlaceholder}</option>
              {chosenSet.document_ids.map((id) => (
                <option key={id} value={id}>
                  {id}
                </option>
              ))}
            </Select>
          </Field>
        )}

        {error && <p className="text-xs text-danger">{error}</p>}
      </div>
    </Dialog>
  )
}
